import { useState, useEffect } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/utils/api";
import { toast } from "sonner";
import { Calculator, Loader2, AlertCircle, Truck, Package } from "lucide-react";

const NETWORK_BADGE = {
  OWN: { label: "OWN NETWORK", cls: "bg-teal-100 text-teal-800 border-teal-200" },
  DELHIVERY_B2B: { label: "DELHIVERY B2B", cls: "bg-blue-100 text-blue-800 border-blue-200" },
  DELHIVERY_B2C: { label: "DELHIVERY B2C", cls: "bg-purple-100 text-purple-800 border-purple-200" },
};

function LineItem({ label, value, sub, bold, divider }) {
  if (value == null || (typeof value === "number" && value === 0 && !sub)) return null;
  return (
    <>
      {divider && <div className="my-2 border-t border-slate-100"></div>}
      <div className={`flex justify-between text-sm ${bold ? "font-bold text-slate-900" : "text-slate-600"} ${sub ? "pl-4 text-xs text-slate-400" : ""}`}>
        <span>{label}</span>
        <span className="font-mono">₹{Number(value).toFixed(2)}</span>
      </div>
    </>
  );
}

function OptionCard({ networkType, option }) {
  const badge = NETWORK_BADGE[networkType];
  const [showSub, setShowSub] = useState(false);

  if (!option?.available) {
    return (
      <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-5 opacity-70" data-testid={`option-${networkType.toLowerCase()}-unavailable`}>
        <div className="flex items-center gap-2 mb-2">
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${badge.cls}`}>{badge.label}</span>
        </div>
        <div className="flex items-start gap-2 text-sm text-slate-500">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>{option?.reason || "Not available"}</span>
        </div>
      </div>
    );
  }

  const isOwn = networkType === "OWN";
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-5 space-y-2" data-testid={`option-${networkType.toLowerCase()}`}>
      <div className="flex items-center justify-between">
        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${badge.cls}`}>{badge.label}</span>
        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
          option.applied_rate_source === "AGREEMENT" ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-600"
        }`}>{option.applied_rate_source || "GLOBAL"}</span>
      </div>

      <div className="space-y-1 pt-1">
        {isOwn ? (
          <>
            <LineItem label="Base Price" value={option.base_price} />
            <button onClick={() => setShowSub((v) => !v)}
              className="text-[10px] text-brand-500 font-semibold uppercase underline">
              {showSub ? "Hide" : "Show"} FM/MM/LM
            </button>
            {showSub && (
              <div className="space-y-0.5">
                <LineItem label="FM cost" value={option.fm_cost} sub />
                <LineItem label="MM cost" value={option.mm_cost} sub />
                <LineItem label="LM cost" value={option.lm_cost} sub />
              </div>
            )}
          </>
        ) : (
          <>
            <LineItem label="Freight" value={option.freight} />
            <LineItem label="LR Charge" value={option.lr_charge} />
            <LineItem label="FSC" value={option.fsc} />
          </>
        )}
        <LineItem label="ODA Surcharge" value={option.oda_surcharge} />
        <LineItem label="Handling Charge" value={option.handling_charge} />
        <LineItem label="Insurance" value={option.insurance_amount} />
        <LineItem label="Platform Fee" value={option.platform_fee} />
        <LineItem label="Taxable Amount" value={option.taxable_amount} divider bold />
        <LineItem label="CGST 9%" value={option.cgst} sub />
        <LineItem label="SGST 9%" value={option.sgst} sub />
        <LineItem label="IGST 18%" value={option.igst} sub />
        <div className="pt-2 mt-1 border-t border-slate-200 flex justify-between items-baseline">
          <span className="text-xs font-bold text-slate-500 uppercase">Total Payable</span>
          <span className="text-lg font-bold text-teal-700 font-mono">₹{Number(option.total_payable || 0).toFixed(2)}</span>
        </div>
      </div>
    </div>
  );
}

export default function PricingConfig() {
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [form, setForm] = useState({
    origin_pincode: "", dest_pincode: "", weight_kg: "",
    product_category: "", service_type: "STANDARD",
    customer_id: "",
    declared_value: "", insurance_opted: false,
    dim_l: "", dim_b: "", dim_h: "",
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/v2/products/categories");
        setProducts((r.data || []).filter((p) => !p.is_blocked));
      } catch { /* ignore */ }
      try {
        const r = await api.get("/v2/customers/profiles", { params: { limit: 200 } });
        setCustomers(r.data?.items || []);
      } catch { /* ignore */ }
    })();
  }, []);

  const setField = (k, v) => {
    setForm((s) => ({ ...s, [k]: v }));
    setFieldErrors((e) => ({ ...e, [k]: "" }));
  };

  const selectedProduct = products.find((p) => p.code === form.product_category);

  const validateForm = () => {
    const errs = {};
    if (!/^\d{6}$/.test((form.origin_pincode || "").trim())) errs.origin_pincode = "6-digit pincode required";
    if (!/^\d{6}$/.test((form.dest_pincode || "").trim())) errs.dest_pincode = "6-digit pincode required";
    const w = parseFloat(form.weight_kg);
    if (!form.weight_kg || isNaN(w) || w <= 0) errs.weight_kg = "Weight must be greater than 0";
    if (!form.product_category) errs.product_category = "Select a product category";
    if (!form.service_type) errs.service_type = "Select a service type";
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const calculate = async () => {
    setError("");
    setResult(null);

    if (!validateForm()) return;

    // Hard guards: BLOCKED + PRIORITY_ONLY
    if (selectedProduct?.is_blocked) {
      setError(`Product '${selectedProduct.name}' is BLOCKED for shipping.`);
      return;
    }
    if (selectedProduct?.service_restriction === "PRIORITY_ONLY"
        && form.service_type !== "PRIORITY") {
      setError(`Product '${selectedProduct.name}' requires PRIORITY service.`);
      return;
    }

    const body = {
      origin_pincode: form.origin_pincode.trim(),
      dest_pincode: form.dest_pincode.trim(),
      weight_kg: parseFloat(form.weight_kg),
      product_category: form.product_category,
      service_type: form.service_type,
    };
    if (form.customer_id) body.customer_id = form.customer_id;
    if (form.declared_value) body.declared_value = parseFloat(form.declared_value);
    if (form.insurance_opted) body.insurance_opted = true;
    if (form.dim_l && form.dim_b && form.dim_h) {
      body.dimensions = {
        l: parseFloat(form.dim_l),
        w: parseFloat(form.dim_b),
        h: parseFloat(form.dim_h),
      };
    }

    setLoading(true);
    try {
      const r = await api.post("/v2/quote/calculate", body);
      setResult(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Quote failed");
    }
    setLoading(false);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 uppercase">Rate Calculator</h1>
          <p className="text-slate-500 text-sm mt-0.5">Generate live quote across Own Network, 3PL B2B, and 3PL B2C.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Form */}
          <div className="bg-white rounded-lg border border-slate-200 p-5 space-y-4">
            <div className="flex items-center gap-2">
              <Calculator className="w-4 h-4 text-brand-500" />
              <h3 className="font-heading text-sm font-bold text-slate-900 uppercase">Shipment Details</h3>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Origin Pincode *</label>
                <input value={form.origin_pincode} onChange={(e) => setField("origin_pincode", e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="500001" data-testid="calc-origin"
                  className={`w-full px-3 py-2 border rounded-sm text-sm focus:outline-none ${fieldErrors.origin_pincode ? "border-red-400 focus:border-red-500" : "border-slate-200 focus:border-brand-500"}`} />
                {fieldErrors.origin_pincode && <p className="text-[10px] text-red-600 mt-1" data-testid="err-origin">{fieldErrors.origin_pincode}</p>}
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Destination Pincode *</label>
                <input value={form.dest_pincode} onChange={(e) => setField("dest_pincode", e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="560001" data-testid="calc-dest"
                  className={`w-full px-3 py-2 border rounded-sm text-sm focus:outline-none ${fieldErrors.dest_pincode ? "border-red-400 focus:border-red-500" : "border-slate-200 focus:border-brand-500"}`} />
                {fieldErrors.dest_pincode && <p className="text-[10px] text-red-600 mt-1" data-testid="err-dest">{fieldErrors.dest_pincode}</p>}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Weight (kg) *</label>
                <input type="number" step="0.01" min="0" value={form.weight_kg}
                  onChange={(e) => setField("weight_kg", e.target.value)}
                  data-testid="calc-weight"
                  className={`w-full px-3 py-2 border rounded-sm text-sm focus:outline-none ${fieldErrors.weight_kg ? "border-red-400 focus:border-red-500" : "border-slate-200 focus:border-brand-500"}`} />
                {fieldErrors.weight_kg && <p className="text-[10px] text-red-600 mt-1" data-testid="err-weight">{fieldErrors.weight_kg}</p>}
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Service Type *</label>
                <select value={form.service_type} onChange={(e) => setField("service_type", e.target.value)}
                  data-testid="calc-service"
                  className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:outline-none focus:border-brand-500 bg-white">
                  <option value="PRIORITY">PRIORITY</option>
                  <option value="STANDARD">STANDARD</option>
                  <option value="EXPRESS">EXPRESS</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Product Category *</label>
              <select value={form.product_category} onChange={(e) => setField("product_category", e.target.value)}
                data-testid="calc-product"
                className={`w-full px-3 py-2 border rounded-sm text-sm focus:outline-none bg-white ${fieldErrors.product_category ? "border-red-400 focus:border-red-500" : "border-slate-200 focus:border-brand-500"}`}>
                <option value="">-- Select --</option>
                {products.map((p) => (
                  <option key={p.code} value={p.code}>
                    {p.name}{p.service_restriction === "PRIORITY_ONLY" ? " (Priority only)" : ""}
                  </option>
                ))}
              </select>
              {fieldErrors.product_category && <p className="text-[10px] text-red-600 mt-1" data-testid="err-product">{fieldErrors.product_category}</p>}
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">L (cm)</label>
                <input type="number" min="0" value={form.dim_l}
                  onChange={(e) => setField("dim_l", e.target.value)}
                  data-testid="calc-dim-l"
                  className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:outline-none focus:border-brand-500" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">B (cm)</label>
                <input type="number" min="0" value={form.dim_b}
                  onChange={(e) => setField("dim_b", e.target.value)}
                  data-testid="calc-dim-b"
                  className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:outline-none focus:border-brand-500" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">H (cm)</label>
                <input type="number" min="0" value={form.dim_h}
                  onChange={(e) => setField("dim_h", e.target.value)}
                  data-testid="calc-dim-h"
                  className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:outline-none focus:border-brand-500" />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Customer (optional)</label>
              <select value={form.customer_id} onChange={(e) => setField("customer_id", e.target.value)}
                data-testid="calc-customer"
                className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:outline-none focus:border-brand-500 bg-white">
                <option value="">-- No customer --</option>
                {customers.map((c) => (
                  <option key={c.customer_id || c.id || c._id} value={c.customer_id || c.id || c._id}>
                    {c.company_name || c.contact_person || c.email}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase mb-1">Declared Value (₹)</label>
                <input type="number" min="0" step="0.01" value={form.declared_value}
                  onChange={(e) => setField("declared_value", e.target.value)}
                  data-testid="calc-declared-value"
                  className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:outline-none focus:border-brand-500" />
              </div>
              {parseFloat(form.declared_value || 0) > 0 && (
                <div className="flex items-end pb-2">
                  <label className="inline-flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={form.insurance_opted}
                      onChange={(e) => setField("insurance_opted", e.target.checked)}
                      data-testid="calc-insurance" />
                    Insurance opted
                  </label>
                </div>
              )}
            </div>

            {selectedProduct?.is_blocked && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-100">
                <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-red-700">
                  Product &apos;{selectedProduct.name}&apos; is BLOCKED. Cannot generate quote.
                </p>
              </div>
            )}
            {selectedProduct?.service_restriction === "PRIORITY_ONLY"
              && form.service_type !== "PRIORITY" && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-100">
                <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-700">
                  &apos;{selectedProduct.name}&apos; requires PRIORITY service. Change service type.
                </p>
              </div>
            )}
            {error && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-100">
                <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-red-700">{error}</p>
              </div>
            )}

            <button onClick={calculate} disabled={loading}
              data-testid="calc-submit"
              className="w-full py-2.5 bg-brand-500 text-white text-sm font-semibold rounded flex items-center justify-center gap-2 hover:bg-brand-600">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calculator className="w-4 h-4" />}
              Calculate
            </button>
          </div>

          {/* Results */}
          <div className="space-y-4">
            {!result ? (
              <div className="bg-slate-50 border border-dashed border-slate-200 rounded-lg p-10 text-center text-slate-400">
                <Truck className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Enter shipment details and click Calculate.</p>
              </div>
            ) : (
              <>
                {!result.own_network_available && (
                  <div className="p-3 rounded-lg bg-slate-100 border border-slate-200 text-xs text-slate-500" data-testid="own-network-unavailable-banner">
                    Own network unavailable for this destination. Use Delhivery options.
                  </div>
                )}
                <OptionCard networkType="OWN" option={result.options?.own_network} />
                <OptionCard networkType="DELHIVERY_B2B" option={result.options?.delhivery_b2b} />
                <OptionCard networkType="DELHIVERY_B2C" option={result.options?.delhivery_b2c} />
              </>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
